import { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { finishLoad, initLoad } from '../../..';
import { IListSlice } from '../../../types';
import deleteFilters from './deleteFilters';
import filter from './filter';
import page from './page';
import refresh from './refresh';
import setFilters from './setFilters';
import sort from './sort';

export default function addListActions(
  builder: ActionReducerMapBuilder<IListSlice>,
) {
  builder.addCase(setFilters.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(setFilters.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId, false);
  });
  builder.addCase(setFilters.fulfilled, (state, { payload, meta }) => {
    state.lists[meta.arg.listId].filters = payload;
    state.lists[meta.arg.listId].areAllFiltersApplied = false;
    finishLoad(state, meta.arg.listId, true, payload);
  });

  builder.addCase(refresh.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(refresh.rejected, (state, payload) => {
    console.error(payload.error);

    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(refresh.fulfilled, (state, { payload, meta }) => {
    state.lists[meta.arg.listId].hasSearched = true;
    state.lists[meta.arg.listId].rows = payload.rows;

    if (payload.rows.length <= 0 && !payload.areSysExce) {
      state.lists[meta.arg.listId].customLabel = window.FINDER_NO_DATA ?? '';
    }
    state.lists[meta.arg.listId].areAllFiltersApplied = true;

    state.lists[meta.arg.listId].pageInfo = payload.pageInfo;

    finishLoad(state, meta.arg.listId);
  });

  builder.addCase(page.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(page.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(page.fulfilled, (state, { payload, meta }) => {
    state.lists[meta.arg.listId].rows = payload.rows;

    state.lists[meta.arg.listId].lastSearchAppliedFilters =
      state.lists[meta.arg.listId].filters?.filter(
        (current) =>
          current.currentValue !== undefined && current.currentValue !== '',
      ).length ?? 0;
    state.lists[meta.arg.listId].pageInfo = payload.pageInfo;
    state.lists[meta.arg.listId].hasSearched = true;
    finishLoad(state, meta.arg.listId);
  });

  builder.addCase(sort.fulfilled, (state, { payload, meta }) => {
    const { listId: fieldId } = meta.arg;
    if (payload) {
      const { columnIndex: eventColumnIndex } = payload;

      state.lists[fieldId].columns = state.lists[fieldId].columns?.map(
        (currentColumn, columnIndex) => {
          if (columnIndex === eventColumnIndex) {
            return {
              ...currentColumn,
              currentSorting: currentColumn.currentSorting === 'A' ? 'D' : 'A',
            };
          }
          return { ...currentColumn, currentSorting: null };
        },
      );

      if (payload.pageInfo && payload.table) {
        if (payload.table.length <= 0) {
          state.lists[meta.arg.listId].customLabel =
            window.FINDER_NO_DATA ?? '';
        }
        state.lists[fieldId].rows = payload.table;
        state.lists[fieldId].pageInfo = payload.pageInfo;
      }
    }
    state.lists[meta.arg.listId].areAllFiltersApplied = true;
    state.lists[meta.arg.listId].hasSearched = true;
    finishLoad(state, meta.arg.listId);
  });
  builder.addCase(sort.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(sort.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });

  builder.addCase(filter.fulfilled, (state, payload) => {
    state.lists[payload.meta.arg.listId].rows = payload.payload?.rows ?? [];
    if (payload.payload?.rows.length <= 0 && !payload.payload.customLabel) {
      state.lists[payload.meta.arg.listId].customLabel =
        window.FINDER_NO_DATA ?? '';
    } else if (
      payload.payload?.rows.length <= 0 &&
      payload.payload.customLabel
    ) {
      state.lists[payload.meta.arg.listId].customLabel =
        payload.payload.customLabel ?? '';
    }
    state.lists[payload.meta.arg.listId].pageInfo = payload.payload.pageInfo;
    state.lists[payload.meta.arg.listId].filters = payload.payload.filters;
    state.lists[payload.meta.arg.listId].areAllFiltersApplied = true;
    state.lists[payload.meta.arg.listId].hasSearched = true;

    finishLoad(
      state,
      payload.meta.arg.listId,
      true,
      payload.payload.filters ?? undefined,
    );
  });
  builder.addCase(filter.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(filter.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });

  builder.addCase(deleteFilters.fulfilled, (state, payload) => {
    if (payload.payload) {
      state.lists[payload.meta.arg.listId].filters = payload.payload?.filters;
    }
    if (payload.payload?.areRequiredFilters) {
      state.lists[payload.meta.arg.listId].rows = [];
    }
    state.lists[payload.meta.arg.listId].hasSearched = false;

    const currentPageInfo = state.lists[payload.meta.arg.listId]?.pageInfo;

    if (
      currentPageInfo &&
      payload.payload?.filters?.some((current) => {
        return current.required;
      })
    ) {
      state.lists[payload.meta.arg.listId].pageInfo = {
        ...currentPageInfo,
        pageCount: '1',
        totalRecords: '0',
        reachedMax: false,
        prefix: '',
        currentPage: '1',
        selectOnlyOne: false,
      };
    }
    state.lists[payload.meta.arg.listId].areAllFiltersApplied = true;
    finishLoad(
      state,
      payload.meta.arg.listId,
      true,
      payload.payload?.filters ?? undefined,
    );
  });
  builder.addCase(deleteFilters.rejected, (state, payload) => {
    console.error(payload.error);
    finishLoad(state, payload.meta.arg.listId);
  });
  builder.addCase(deleteFilters.pending, (state, payload) => {
    initLoad(state, payload.meta.arg.listId);
  });
}
